import { PLATFORM_ERROR_CODES, type PlatformErrorCode } from '../../app/modules/types/platform'

/**
 * A platform API failure, split the way the UI has to show it:
 *
 * - `fieldErrors` — DRF `{field: [msg]}` and `non_field_errors`, shown under the field;
 * - `code` + `detail` — a refused operation (`400 {detail, code}`) or the `409 in_use`
 *   on delete, shown as a banner with the code's own wording;
 * - `status` — 403 is the "superusers only" state, 404 is not-found.
 *
 * A body that is not JSON (a gateway's HTML 502, say) carries no `detail`: the
 * banner then falls back to its generic line rather than printing markup.
 */
export interface PlatformFailure {
  status: number | null
  code: PlatformErrorCode | null
  detail: string | null
  fieldErrors: Record<string, string[]>
}

interface ErrorLike {
  message?: string
  response?: { status?: number; data?: unknown }
}

const CODES: ReadonlySet<string> = new Set(PLATFORM_ERROR_CODES)
export const isPlatformErrorCode = (value: unknown): value is PlatformErrorCode =>
  typeof value === 'string' && CODES.has(value)

/**
 * Every string inside a DRF error value, however deep. A nested serializer's
 * errors arrive as `[{}, {accessibility: ["…"]}]`; the messages are what the
 * reader needs, and `String(object)` is not one.
 */
function messagesOf(value: unknown): string[] {
  if (typeof value === 'string') return value.trim() ? [value] : []
  if (Array.isArray(value)) return value.flatMap(messagesOf)
  if (value && typeof value === 'object') return Object.values(value).flatMap(messagesOf)
  return []
}

export function readPlatformError(error: unknown): PlatformFailure {
  const { response, message }: ErrorLike = typeof error === 'object' && error !== null ? error : {}
  const status = response?.status ?? null
  const data = response?.data
  const fieldErrors: Record<string, string[]> = {}
  let code: PlatformErrorCode | null = null
  let detail: string | null = null

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    for (const [key, value] of Object.entries(data)) {
      if (key === 'code') {
        if (isPlatformErrorCode(value)) code = value
        continue
      }
      if (key === 'detail' || key === 'message') {
        if (typeof value === 'string' && value.trim()) detail = value
        continue
      }
      const messages = messagesOf(value)
      if (messages.length > 0) fieldErrors[key] = messages
    }
  } else if (Array.isArray(data)) {
    const messages = messagesOf(data)
    if (messages.length > 0) detail = messages[0]
  } else if (typeof data === 'string' && data.trim() && !/^\s*</.test(data)) {
    detail = data
  } else if (!response) {
    // No response at all: axios's own message is the only thing to show.
    detail = message ?? null
  }
  return { status, code, detail, fieldErrors }
}

/** The messages for one field, joined, for the control's error slot. */
export function fieldErrorOf(fieldErrors: Record<string, string[]>, field: string): string | undefined {
  const messages = fieldErrors[field]
  return messages && messages.length > 0 ? messages.join(' ') : undefined
}

export const isForbidden = (failure: PlatformFailure): boolean => failure.status === 403
export const isNotFound = (failure: PlatformFailure): boolean => failure.status === 404

/**
 * React Query retry policy for platform reads: a 4xx is an answer (403 is the
 * "superusers only" state, 404 is not-found), so retrying it only delays the page
 * that explains it. Transport and 5xx failures keep one retry.
 */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const status = readPlatformError(error).status
  if (status !== null && status >= 400 && status < 500) return false
  return failureCount < 1
}
